import type { Indicator } from './indicator';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon, CloseIcon, ReloadIcon } from '../../icons';
import { classNames } from '../../utils/class-names';
import { IconButton, LinkStyleButton } from '../buttons';
import { Tooltip } from '../tooltip';

import styles from './progress-indicator.module.css';

/**
 * @param {('TRANSFERRING' | 'UPLOADING' | 'PROCESSING' | 'FAILED' | 'PARTIALLY_FAILED' | 'PASSED' | 'FINISHED')} status The current status that controls the progress style and labels
 * @example
 * UPLOADING: The file is being uploaded
 * PROCESSING: The file is being processed
 * FAILED: The process has failed
 * PASSED: The process reached 100%
 * FINISHED: The final status of the indicator without the progress bar
 * @param {number} progressPercentage The percentage of progress displayed as a bar
 * @param {ReactNode} [icon] The icon displayed on the left side with the status label
 * @param {string} label The text displayed on the left side the describe the process name or status
 * @param {string} [buttonLabel] The button name displayed in the tooltip
 * @param {string} [buttonIcon] The icon displayed on the right side of the progress bar
 * @param {string} [barLabel] Name displayed on the right side of the progress bar
 * @param {string} [id] An optional id to set to the progress-bar.
 * @param {boolean} [showPercentage] An optional flag to display the percentage number
 * @param {boolean} [showBarLabel] An optional flag to display the bar label
 * @param {string} [className] An optional class name to add to the container
 * @param {Function} [onButtonClicked] The function called when the right button is clicked
 */
export function ProgressIndicator({
  progressPercentage,
  icon,
  status,
  label,
  buttonLabel,
  buttonIcon,
  barLabel,
  id,
  showPercentage,
  showBarLabel,
  className,
  onButtonClicked,
}: Indicator) {
  const { t } = useTranslation();
  let displayButtonIcon;
  const displayBarLabel: string = barLabel || t(`progressIndicator.status.${status}`);

  switch (status) {
    case 'TRANSFERRING':
    case 'PROCESSING':
    case 'UPLOADING':
      displayButtonIcon = <CloseIcon />;
      break;
    case 'FAILED':
    case 'PARTIALLY_FAILED':
      displayButtonIcon = <ReloadIcon />;
      break;
    case 'PASSED':
    case 'COMPLETED':
      displayButtonIcon = <CheckIcon />;
      break;
  }

  progressPercentage > 100 && (progressPercentage = 100);

  return (
    <div className={classNames(styles.container, className, status === 'FINISHED' && styles.finished)}>
      {(icon || label) && (
        <div className={styles.top}>
          {icon && icon}

          <span data-testid={id ? `${id}-label` : 'progress-bar-label'} className={styles.label}>
            {label}
          </span>

          {status === 'FINISHED' && buttonLabel && onButtonClicked && (
            <LinkStyleButton type="button" small onClick={onButtonClicked}>
              {buttonLabel}
            </LinkStyleButton>
          )}
        </div>
      )}

      {status !== 'FINISHED' && (
        <div className={styles.bottom}>
          <div
            data-testid={id || 'progress-bar'}
            className={classNames(styles.bar, styles[status])}
            style={{ '--mi-progress-bar-line-width': `${progressPercentage}%` } as React.CSSProperties}
          />

          {showPercentage && (
            <span data-testid={id ? `${id}-percentage` : 'progress-bar-percentage'} className={styles.barPercentage}>
              {progressPercentage}&nbsp;%
            </span>
          )}

          {showBarLabel && (
            <span
              data-testid={id ? `${id}-percentage-label` : 'progress-bar-percentage-label'}
              className={styles.barLabel}
            >
              {displayBarLabel}
            </span>
          )}

          {onButtonClicked && (buttonIcon || displayButtonIcon) && (
            <Tooltip label={buttonLabel} position={'right'}>
              <IconButton
                className={styles.button}
                aria-label={buttonLabel || ''}
                onClick={onButtonClicked}
                type="button"
                naked
                small
              >
                {buttonIcon || displayButtonIcon}
              </IconButton>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
}
